import {useState} from 'react'
import {Box, Image, Text} from '@primer/react-brand'
import {MarkGithubIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import type {Asset} from '@github-ui/swp-core/schemas/contentful/contentTypes/asset'
import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import {VideoIframePlayIcon} from '../shared/VideoIcons'
import styles from './VideoIframe.module.css'

type VideoIframeProps = {
  videoSource: string
  optionPoster?: Asset
}

/**
 * Renders an external video (e.g. YouTube) as an embedded iframe.
 * When a poster image is provided, shows a thumbnail with a play button;
 * clicking it reveals the iframe with autoplay.
 */
export function VideoIframe({videoSource, optionPoster}: VideoIframeProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const heading = optionPoster?.fields.description ?? 'External video'

  return !optionPoster || isPlaying ? (
    <Box className={clsx('position-relative width-full overflow-hidden', styles.contentContainer)}>
      {/* eslint-disable-next-line @eslint-react/dom-no-missing-iframe-sandbox */}
      <iframe
        className="border-0 width-full height-full position-absolute top-0 left-0"
        src={`${videoSource}${optionPoster ? `${videoSource.includes('?') ? '&' : '?'}autoplay=1` : ''}`}
        title={heading}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </Box>
  ) : (
    <Box className={clsx('position-relative width-full', styles.contentContainer)}>
      <Image
        as="picture"
        src={`${optionPoster.fields.file.url}?fm=webp`}
        sources={getImageSources(optionPoster.fields.file.url, {maxWidth: MAX_CONTENT_WIDTH})}
        alt={optionPoster.fields.description || ''}
        className={clsx('d-block', styles.posterImage)}
        loading="lazy"
      />
      {optionPoster.fields.description ? (
        <Box
          padding={{narrow: 12, regular: 16}}
          className={clsx(
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            'position-absolute top-0 left-0 width-full d-grid flex-items-center flex-justify-start',
            styles.posterImageTitleContainer,
          )}
        >
          <MarkGithubIcon size={32} className={styles.posterBrandIcon} />
          <Text size="200" weight="medium" className={styles.posterImageTitleText}>
            {optionPoster.fields.description}
          </Text>
        </Box>
      ) : null}
      <button
        className={clsx(
          'position-absolute top-0 left-0 width-full border-0 d-flex flex-justify-center flex-items-center',
          styles.playButton,
        )}
        onClick={() => setIsPlaying(true)}
        aria-label={`Play video. Thumbnail description: ${optionPoster.fields.description || 'No description available'}`}
      >
        <VideoIframePlayIcon className={styles.playIcon} />
      </button>
    </Box>
  )
}
